// Pure multi-zone layout planning (docs/04 §Zones).
//
// A manifest MAY carry `zones` — a layout of independent rectangles, each with
// its own item list. Composition lives HERE in the app (not in player-core, which
// stays a zone-agnostic single-timeline renderer): this module turns each zone
// into a synthetic single-timeline `Manifest` the existing PlaybackEngine can
// play as-is. Kept side-effect free so the mapping is exhaustively unit-testable.
//
// Rules (server-validated; the player tolerates violations defensively):
//   - `main`  → keeps the manifest's real scheduled top-level `timeline`/`fallback`
//     (window logic UNCHANGED; the zone's own `timeline` mirrors it and is ignored);
//     overlay is stripped because the overlay layer is composed once ABOVE all
//     zones, not per zone.
//   - non-main → plays its own windowed `timeline` (a `ManifestWindow[]`, same shape
//     as the top-level — the engine finds the active window and loops its items),
//     with any `video` item dropped (video is allowed only in `main`).
// Absent/empty `zones` → the app renders exactly today's single fullscreen engine.

import type { Manifest, ManifestZone, ZoneId, ZoneRect } from './types.ts';

export interface ZonePlacement {
  id: ZoneId;
  rect: ZoneRect;
  /** True for the `main` zone — driven by the scheduled top-level timeline. */
  isMain: boolean;
  /** A single-timeline manifest this zone's engine plays directly. */
  manifest: Manifest;
}

/** True when the manifest declares a non-empty multi-zone layout. */
export function hasZones(m: Manifest): boolean {
  return Array.isArray(m.zones) && m.zones.length > 0;
}

/**
 * Stable identity of the zone LAYOUT (ordered ids + rects) — not the content.
 * The renderer rebuilds its engines only when this changes; a plain content swap
 * (same rectangles) reuses the engines so playback isn't interrupted.
 */
export function zoneLayoutKey(m: Manifest): string {
  if (!hasZones(m)) return 'single';
  return (m.zones ?? [])
    .map((z) => `${z.id}:${z.rect_pct.x},${z.rect_pct.y},${z.rect_pct.w},${z.rect_pct.h}`)
    .join('|');
}

/** One placement per declared zone. Empty when the manifest has no zones. */
export function zonePlan(m: Manifest): ZonePlacement[] {
  return (m.zones ?? []).map((z) => {
    const isMain = z.id === 'main';
    return {
      id: z.id,
      rect: z.rect_pct,
      isMain,
      manifest: isMain ? mainZoneManifest(m) : loopingZoneManifest(m, z),
    };
  });
}

/** The main zone plays the real schedule, minus the (separately composed) overlay + zones. */
function mainZoneManifest(m: Manifest): Manifest {
  // Drop overlay + zones so the engine renders no per-zone overlay and doesn't
  // recurse into zones; the windowed timeline + fallback stay exactly as today.
  const { overlay: _overlay, zones: _zones, ...rest } = m;
  void _overlay;
  void _zones;
  return rest;
}

/**
 * A non-main zone: play its OWN windowed timeline (same shape as the top-level
 * one, so the engine's "active window → loop items" logic applies), with any video
 * item dropped per-window (video is main-only). No overlay (composed above zones).
 */
function loopingZoneManifest(m: Manifest, zone: ManifestZone): Manifest {
  const timeline = zone.timeline.map((w) => ({
    ...w,
    items: w.items.filter((it) => it.type !== 'video'),
  }));
  return {
    version: m.version,
    min_player_version: m.min_player_version,
    screen: m.screen,
    poll_interval_s: m.poll_interval_s,
    valid_until: m.valid_until,
    timeline,
    fallback: { items: [] },
  };
}
